import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { FullMeetingFragment, ObjectiveStatus, ObjectiveDto } from '../../../types'
import { ObjectiveList, ObjectiveListType } from './lists/ObjectivesList'
import { filterObjectivesByTypeStatusAndRefYear } from './util'
import PerSpacer from '../../../components/custom_components/Spacer'
import PerText from '../../../components/custom_components/Text'
import PerFlexRow from '../../../components/custom_components/FlexRow'
import useObjectiveWithModalInMeeting from '../../../graphql/objectives/useObjectiveWithModalInMeeting'
import { TargetsModal } from '../../../components/custom_components/TargetsModal/TargetsModal'
import { useTargetsModalStore } from '../../../components/custom_components/TargetsModal/TargetsModalStore'
import { TargetModalType } from '../../../components/custom_components/TargetsModal/types'
import { BonusDistributionModal } from '../../../components/custom_components/BonusDistributionModal/BonusDistributionModal'
import PerButton from '../../../components/custom_components/Button'
import AppraisalPageDescription from '../appraisal-flow/shared/AppraisalPageDescription'
import { IconIndicator } from '../../../components/custom_components/IconIndicator/IconIndicator'
import { isDefined } from '../../../types.helper'
import { retrieveCurrencyConversionRate } from '../../../shared/currencyConversionUtil'
import { useCurrency } from '../../../context/currency-context'

interface Props {
  meeting: FullMeetingFragment
  objectives: ObjectiveDto[]
  hasBonusOrWillHaveBonus: boolean
  isAppraisalUpdate?: boolean
  existingWrongNewTargets: boolean
}

export const ManagerNewObjectives: FC<Props> = ({
  meeting,
  objectives,
  hasBonusOrWillHaveBonus,
  isAppraisalUpdate,
  existingWrongNewTargets,
}) => {
  const [t] = useTranslation()
  const {
    id: meetingId,
    employeePrepared,
    targetsCalculationHelper: { newTargetsReferenceYear },
  } = meeting

  const openTargetModal = useTargetsModalStore((state) => state.openTargetModal)
  const { handleObjectiveDismiss, handleObjectiveReactivate, handleObjectiveDuplicate } = useObjectiveWithModalInMeeting({
    meetingId,
  })

  const { currency: currentContractCurrency, currencyConversionData } = useCurrency()

  let status = isAppraisalUpdate
    ? [
        ObjectiveStatus.Approved,
        ObjectiveStatus.Proposal,
        ObjectiveStatus.ProposalFromManager,
        ObjectiveStatus.MarkedAsDone,
        ObjectiveStatus.DismissedPending,
      ]
    : [ObjectiveStatus.Proposal, ObjectiveStatus.ProposalFromManager, ObjectiveStatus.Approved, ObjectiveStatus.MarkedAsDone]

  if (!employeePrepared) {
    // we filter proposal targets if employee didn't confirm preparation (reviewer can't see these targets so they aren't valid)
    status = status.filter((status) => status !== ObjectiveStatus.Proposal)
  }

  const monetaryObjectives = filterObjectivesByTypeStatusAndRefYear(objectives, true, status, newTargetsReferenceYear)
  const developmentObjectives = filterObjectivesByTypeStatusAndRefYear(objectives, false, status, newTargetsReferenceYear)

  const handleObjectiveClick = (objective: ObjectiveDto | undefined) => {
    if (!objective) {
      openTargetModal(TargetModalType.appraisalNewTarget)
    } else {
      const conversioneRate: number | undefined = !!objective.currency
        ? retrieveCurrencyConversionRate(objective.currency, currentContractCurrency, currencyConversionData)
        : undefined
      if (
        objective.status === ObjectiveStatus.Proposal ||
        objective.status === ObjectiveStatus.ProposalFromManager ||
        (!objective.id && [ObjectiveStatus.Approved, ObjectiveStatus.MarkedAsDone].includes(objective.status))
      ) {
        openTargetModal(TargetModalType.appraisalNewTarget, objective, conversioneRate)
      } else if (objective.status === ObjectiveStatus.Approved || objective.status === ObjectiveStatus.MarkedAsDone) {
        openTargetModal(TargetModalType.appraisalEditActiveTarget, objective, conversioneRate)
      } else if (objective.status === ObjectiveStatus.Dismissed || objective.status === ObjectiveStatus.DismissedPending) {
        openTargetModal(TargetModalType.viewTargetInMeeting, objective, conversioneRate)
      }
    }
  }

  const onObjectiveDeletion = (objective?: ObjectiveDto): void => {
    if (!!objective) {
      openTargetModal(
        TargetModalType.deleteTarget,
        objective,
        isDefined(objective.targetAmount)
          ? retrieveCurrencyConversionRate(objective.currency!, currentContractCurrency, currencyConversionData)
          : undefined,
      )
    }
  }

  const handleObjectiveDuplicateAndDismissOrDelete = (objective?: ObjectiveDto) => {
    if (!!objective) {
      openTargetModal(
        TargetModalType.duplicateAndDismissOrDeleteTarget,
        objective,
        !!objective && isDefined(objective.targetAmount)
          ? retrieveCurrencyConversionRate(objective.currency!, currentContractCurrency, currencyConversionData)
          : undefined,
      )
    }
  }
  return (
    <>
      <PerFlexRow justify="space-between" align="middle" mt="l" mb="s">
        <PerFlexRow align="middle">
          <PerText appearance="heading" color="primary">
            {t('okr.instructions.manager.create')}
          </PerText>
          {existingWrongNewTargets && (
            <>
              <PerSpacer ml="xs" />
              <IconIndicator
                icon="warning_fill"
                background="error"
                variant="clickable"
                size="l"
                tooltip={t('appraisalMeeting.stepConclusion.disabledButtonLabel.existingWrongNewTargets')}
              />
            </>
          )}
        </PerFlexRow>
        <PerButton
          text={t('appraisalMeeting.buttons.addTargets')}
          color="success"
          before="add_stroke"
          variant="pastel"
          type="button"
          tooltip={t('appraisalMeeting.buttons.newGoals')}
          onClick={() => openTargetModal(TargetModalType.appraisalNewTarget)}
          data-e2e-add-more-targets-button
        />
      </PerFlexRow>

      <AppraisalPageDescription
        description={t(`appraisalMeeting.stepNewGoals.${meeting.managerPrepared ? 'pageDescriptionMeeting' : 'pageDescriptionManager'}`)}
        mb="s"
      />

      {(hasBonusOrWillHaveBonus || !!monetaryObjectives.length) && (
        <PerSpacer mb="m">
          <ObjectiveList
            listType={ObjectiveListType.Monetary}
            objectives={monetaryObjectives}
            handleObjectiveClicked={(_, objective) => handleObjectiveClick(objective)}
            handleObjectiveDelete={(objective?: ObjectiveDto) => onObjectiveDeletion(objective)}
            handleObjectiveDismiss={handleObjectiveDismiss}
            handleObjectiveReactivate={handleObjectiveReactivate}
            handleObjectiveDuplicate={handleObjectiveDuplicate}
            showStatus={isAppraisalUpdate}
            showToBePaidAndBonus={isAppraisalUpdate}
            showTasks={!isAppraisalUpdate}
            canDuplicate
            handleObjectiveDuplicateAndDismissOrDelete={(objective) => handleObjectiveDuplicateAndDismissOrDelete(objective)}
          />
        </PerSpacer>
      )}

      <ObjectiveList
        listType={ObjectiveListType.Development}
        objectives={developmentObjectives}
        handleObjectiveClicked={(_, objective) => handleObjectiveClick(objective)}
        handleObjectiveDelete={(objective?: ObjectiveDto) => onObjectiveDeletion(objective)}
        handleObjectiveDismiss={handleObjectiveDismiss}
        handleObjectiveReactivate={handleObjectiveReactivate}
        handleObjectiveDuplicate={handleObjectiveDuplicate}
        showStatus={isAppraisalUpdate}
        showToBePaidAndBonus={isAppraisalUpdate}
        showTasks={!isAppraisalUpdate}
        canDuplicate
      />
      <BonusDistributionModal />
      <TargetsModal />
    </>
  )
}
